import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { isEqual } from 'lodash';

import Popover from 'components/popover/Popover';
import { withModal } from 'components/modal/ModalHOC';
import ActionLink from 'components/buttons/action-link/ActionLink';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import AddToListsModal from 'components/add-to-lists-modal/AddToListsModal';
import ActionButtonDropdown from 'components/buttons/action-button-dropdown/ActionButtonDropdown';

import ListsCreateEditModal from 'modules/lists/components/ListsCreateEditModal';
import InfluencerModalActionsMenu from 'modules/influencers/components/modals/modal-actions-menu/InfluencerModalActionsMenu';
import DiscoveryCreatorsFilterPopover from 'modules/freemium/components/discovery-creators-filter-popover/DiscoveryCreatorsFilterPopover';

import { info } from 'redux-store/reducers/common-reducer';
import { fetchInfluencersMeta } from 'modules/influencers/reducers/influencersMeta';
import { addInfluencerToLists, removeInfluencerFromLists } from 'modules/lists/reducers/lists';

import { __t, applyTranslations } from 'utils/language';
import { isInfluencerProfileRejected } from 'utils/profile';
import { sanitizeGenericHtml } from 'utils/sanitize';

import './list-button.scss';

import type { InfluencerTile, RecentsResultEnhanced } from 'types';

applyTranslations(require('./list-button.i18n.json'));

type OwnProps = {
  orgId: number,
  copyFromListId?: number | null,
  influencer: InfluencerTile,
  autoOpenModal: boolean,
  isFreemium?: boolean,
  onDeleteFromCurrentList?: () => void
};

type DispatchProps = {
  dispatchAddInfluencerToList: (d: number | string | undefined, c: number, b: number, a?: number | null) => Promise<void>,
  dispatchRemoveInfluencerFromList: (d: number | string | undefined, c: number, b: number, a?: string | null) => Promise<void>,
  dispatchUpdateInfluencerMeta: (a: number) => Promise<void>,
  dispatchNotification: (a: string) => void
};

type Props = OwnProps & DispatchProps;

type State = {
  isShowingFullModal: boolean,
  isShowNew: boolean
};

class ListButton extends React.Component<Props, State> {
  state = {
    isShowingFullModal: false,
    isShowNew: false
  };

  shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    const {
      orgId,
      copyFromListId,
      influencer,
      autoOpenModal
    } = this.props;

    return orgId !== nextProps.orgId
      || copyFromListId !== nextProps.copyFromListId
      || autoOpenModal !== nextProps.autoOpenModal
      || influencer?.id !== nextProps.influencer?.id
      || !isEqual(this.state, nextState);
  }

  handleActionsMenuItemClick = async (item: RecentsResultEnhanced | undefined | null, isSelected: boolean): Promise<void> => {
    if (!item) {
      return;
    }

    if (isSelected) {
      await this.handleRemove(item);
    } else {
      await this.handleAdd(item);
    }
  };

  handleUpdateInfluencerMeta = () => {
    const { influencer, dispatchUpdateInfluencerMeta } = this.props;
    dispatchUpdateInfluencerMeta(influencer.id);
  };

  async handleAdd(item: RecentsResultEnhanced): Promise<void> {
    const {
      influencer,
      copyFromListId,
      dispatchNotification,
      dispatchAddInfluencerToList
    } = this.props;

    if (!item.organizationId) {
      return;
    }

    await dispatchAddInfluencerToList(item.id, item.organizationId, influencer.id, copyFromListId);
    this.handleUpdateInfluencerMeta();
    dispatchNotification(__t('list_button.tooltip_added'));
  }

  async handleRemove(item: RecentsResultEnhanced): Promise<void> {
    const {
      influencer,
      copyFromListId,
      onDeleteFromCurrentList,
      dispatchNotification,
      dispatchRemoveInfluencerFromList
    } = this.props;

    if (!item.memberId || !item.organizationId) {
      return;
    }

    if (item.id === copyFromListId && onDeleteFromCurrentList) {
      // when removing from current list deletion will be handled with confirmation dialog
      onDeleteFromCurrentList();
    } else {
      await dispatchRemoveInfluencerFromList(item.id, item.organizationId, influencer.id, String(item.memberId));
      this.handleUpdateInfluencerMeta();
      dispatchNotification(__t('list_button.tooltip_removed'));
    }
  }

  handleModalOpenCreated = () => {
    this.setState({
      isShowNew: false,
      isShowingFullModal: true
    });
  };

  handleModalOpenCreateNew = () => {
    this.setState({
      isShowNew: true,
      isShowingFullModal: true
    });
  };

  handleModalClose = () => {
    this.setState({
      isShowingFullModal: false
    });
  };

  renderIconPopover(children: React.ReactNode): React.ReactNode {
    return (
      <DiscoveryCreatorsFilterPopover
        triggerComponent={children}
      />
    );
  }

  renderIcon(isActive: boolean) {
    const { isFreemium } = this.props;

    const triggerClasses = classnames('list-button__trigger', {
      'list-button__trigger--blocked': isFreemium
    });

    const iconClasses = classnames('list-button__icon', {
      'list-button__icon--blocked': isFreemium
    });

    return (
      <div className={triggerClasses}>
        <div className={iconClasses}>
          <SVGDynamicIcon
            name="list-custom-bg"
            customColor={isActive ? 'primary' : 'grey'}
            clickable={!isFreemium}
            width={19}
            height={19}
          />
        </div>
      </div>
    );
  }

  renderOpenModalLink(): React.ReactNode {
    return (
      <ActionLink
        color="secondary"
        weight="bold"
        onClick={this.handleModalOpenCreated}
      >
        {__t('list_button.action_open_modal')}
      </ActionLink>
    );
  }

  renderCreateNewCampaignLink(): React.ReactNode {
    return (
      <ActionLink
        color="secondary"
        weight="bold"
        onClick={this.handleModalOpenCreateNew}
      >
        {__t('list_button.action_create_new')}
      </ActionLink>
    );
  }

  renderModal(): React.ReactNode {
    const { orgId, copyFromListId, influencer, autoOpenModal } = this.props;
    const { isShowNew, isShowingFullModal } = this.state;

    // have to do hack with autoOpen modal
    // otherwise modal root component will be unmounted immediately when action button will be hidden on blur
    const DivWithModal = withModal(<div />, {
      modalContent: (isShowNew) ? ListsCreateEditModal : AddToListsModal,
      onModalClosed: this.handleModalClose,
      autoOpenModal: true
    });

    if (!isShowingFullModal && !autoOpenModal) {
      return null;
    }

    if (isShowNew) {
      return (
        <DivWithModal
          modalContentProps={{
            title: __t('list_button.modal_title_create'),
            organizationId: orgId
          }}
        />
      );
    }

    return (
      <DivWithModal
        modalContentProps={{
          copyFromListId,
          influencerProfile: influencer
        }}
      />
    );
  }

  render(): React.ReactNode {
    const { orgId, copyFromListId, influencer, isFreemium } = this.props;

    const isInfluencerRejected = isInfluencerProfileRejected(influencer);
    const popoverMsg = isInfluencerRejected
      ? __t('list_button.tooltip_profile_rejected')
      : __t('list_button.tooltip_lists');
    const triggerClasses = classnames('list-button__popover-trigger', {
      'list-button__popover-trigger--blocked': isFreemium
    });
    const popoverClasses = classnames('list-button__popover', {
      'list-button__popover--compact': !isInfluencerRejected
    });

    return (
      <div>
        {this.renderModal()}
        <ActionButtonDropdown
          name="list-button__dropdown"
          items={[]}
          withCenteredMenu
          disabled={isInfluencerRejected}
          blocked={isFreemium}
          customButton={(isOpen) => (
            <Popover
              classNameWrapper="list-button__popover-trigger-wrapper"
              classNameTrigger={triggerClasses}
              classNamePopover={popoverClasses}
              triggerComponent={
                isFreemium
                  ? this.renderIconPopover(this.renderIcon(isOpen))
                  : this.renderIcon(isOpen)
              }
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeGenericHtml(popoverMsg)
                }}
              />
            </Popover>
          )}
          CustomMenu={(
            <InfluencerModalActionsMenu
              resourceType="creator-lists"
              actionType="lists"
              metaKey="lists"
              orgId={orgId}
              activeItemId={copyFromListId}
              influencerProfile={influencer}
              onItemClick={this.handleActionsMenuItemClick}
              openModalTrigger={this.renderOpenModalLink()}
              createNewTrigger={this.renderCreateNewCampaignLink()}
            />
          )}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchAddInfluencerToList: (
    listId: number | string | undefined,
    organizationId: number,
    influencerId: number,
    copyFromListId?: number | null
  ) => dispatch(addInfluencerToLists([listId], organizationId, influencerId, copyFromListId)),

  dispatchRemoveInfluencerFromList: (
    listId: number | string | undefined,
    organizationId: number,
    influencerId: number,
    currentListId?: string | null
  ) => dispatch(removeInfluencerFromLists([listId], organizationId, influencerId, currentListId)),

  dispatchUpdateInfluencerMeta: (influencerId: number) =>
    dispatch(fetchInfluencersMeta([influencerId])),

  dispatchNotification: (message: string) => dispatch(info(message))
});

export default connect(undefined, mapDispatchToProps)(ListButton);
